
import { useState, useEffect, useCallback } from 'react';
import { useTimetableStore } from '../store/timetableStore';
import { toast } from 'sonner';
import { TeacherTimetable } from '@/types/timetable';

interface UseTimetableDataReturn {
  timetableData: TeacherTimetable[] | null;
  isLoading: boolean;
  error: Error | null;
  refreshData: () => Promise<boolean>;
  isRefreshing: boolean;
  hasClassesToday: () => boolean;
}

export const useTimetableData = (): UseTimetableDataReturn => {
  const { timetableData, fetchTimetableData } = useTimetableStore();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('Loading timetable data...');
      await fetchTimetableData();
      console.log('Timetable data loaded successfully');
    } catch (err) {
      console.error('Error fetching timetable data:', err);
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
    } finally {
      setIsLoading(false);
    }
  }, [fetchTimetableData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Listen for sync events from background sync
  useEffect(() => {
    const handleTimetableDataChanged = () => {
      console.log('Timetable data changed event received, refreshing...');
      loadData();
    };

    window.addEventListener('timetableDataChanged', handleTimetableDataChanged);

    return () => {
      window.removeEventListener('timetableDataChanged', handleTimetableDataChanged);
    };
  }, [loadData]);

  const refreshData = useCallback(async (): Promise<boolean> => {
    if (isRefreshing) {
      return false;
    }
    
    setIsRefreshing(true);
    
    try {
      console.log('Refreshing timetable data...');
      await fetchTimetableData();
      setError(null);
      console.log('Timetable data refreshed successfully');
      return true;
    } catch (err) {
      console.error('Error refreshing timetable data:', err);
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      return false;
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchTimetableData, isRefreshing]);

  const hasClassesToday = useCallback((): boolean => {
    if (!timetableData || !Array.isArray(timetableData)) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    for (const teacher of timetableData) {
      if (!teacher.classes || !Array.isArray(teacher.classes)) continue;
      
      for (const classItem of teacher.classes) {
        try {
          const classDate = new Date(classItem.startTime);
          if (classDate >= today && classDate < tomorrow) {
            return true;
          }
        } catch (error) {
          console.error('Error parsing date:', error);
        }
      }
    }
    
    return false;
  }, [timetableData]);

  return {
    timetableData,
    isLoading,
    error,
    refreshData,
    isRefreshing,
    hasClassesToday
  };
};
